import { regular } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import chevronLeft from "assets/images/v2/chevron-left.svg";
import Drawer, { DrawerType } from "components/ui/drawer/Drawer";
import ToastMessage from "components/ui/toast-message/ToastMessage";
import { Button } from "components/v2/button/Button";
import DocumentPreview from "components/v2/document-preview/DocumentPreview";
import { Spinner } from "components/v2/spinner/spinner";
import {
  getApplicationRoleType,
  getCurrentUserId,
  getDealId,
  getEmailAddress,
  getEncompassLoanId,
  getLoanId,
  getLoanNumber,
  getLoanOfficerId,
  getLoanPurpose,
  getNhDealApplicationId,
  getProspectId,
  getUserRole,
} from "features/authSlice";
import useDesktopChecker from "hooks/useDesktopChecker";
import useTabletChecker from "hooks/useTabletChecker";
import { mapUqualBorrowerType } from "hooks/useUqualFullStoryTrackingProps";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getLoanProgress } from "pages/dashboard/store/dashboardSlice";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { getLoanOfficerDetail } from "services/common/loanOfficerSlice";
import { DASHBOARD_SESSION_ID } from "utilities/Constant";
import { base64ToUint8Array } from "utilities/file";
import {
  clearPersistedWireFraudFullStoryEvent,
  hasPersistedWireFraudFullStoryEvent,
  persistWireFraudFullStoryEvent,
} from "utilities/wireFraudFullStory";
import {
  canTrackWireFraudFullStory,
  sessionWireFraudEventKeys,
  trackFSWireFraudTaskCompleted,
  trackFSWireFraudTaskStarted,
  WIRE_FRAUD_FS_EVENT,
} from "utilities/full-story";
import {
  WIRE_FRAUD_COMPLETED_DESCRIPTION,
  WIRE_FRAUD_DETAIL_DESCRIPTION,
  WIRE_FRAUD_DOCUMENT_TITLE,
  WIRE_FRAUD_DOCUMENT_UNAVAILABLE,
  WIRE_FRAUD_DOCUMENT_UNAVAILABLE_DESCRIPTION,
  WIRE_FRAUD_SAVE_ERROR_MESSAGE,
  WIRE_FRAUD_TASK_TITLE,
} from "utilities/wireFraud";
import {
  useCompleteWireFraudTaskMutation,
  useLazyRetrieveWireFraudDocumentQuery,
} from "../../dashboardService";
import "./WireFraudPanel.scss";

interface WireFraudPanelProps {
  readonly setShowUpload: (show: boolean) => void;
  readonly onAcknowledged?: () => Promise<void> | void;
  readonly isCompleted?: boolean;
}

const WireFraudSaveErrorToast = memo(function WireFraudSaveErrorToast() {
  return (
    <ToastMessage
      message={WIRE_FRAUD_SAVE_ERROR_MESSAGE}
      state="error"
      timeout={3000}
      position="top-right"
    />
  );
});

const WireFraudPanel = ({
  setShowUpload,
  onAcknowledged,
  isCompleted = false,
}: WireFraudPanelProps) => {
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const userId = useSelector(getCurrentUserId);
  const email = useSelector(getEmailAddress);
  const dealId = useSelector(getDealId);
  const prospectId = useSelector(getProspectId);
  const encompassLoanId = useSelector(getEncompassLoanId);
  const loanId = useSelector(getLoanId);
  const loanNumber = useSelector(getLoanNumber);
  const loanPurpose = useSelector(getLoanPurpose);
  const loanProgress = useSelector(getLoanProgress);
  const userRole = useSelector(getUserRole);
  const applicationRoleType = useSelector(getApplicationRoleType);
  const loanOfficer = useSelector(getLoanOfficerDetail);
  const loanOfficerId = useSelector(getLoanOfficerId);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const [retrieveWireFraudDocument, { isFetching }] =
    useLazyRetrieveWireFraudDocumentQuery();
  const [completeWireFraudTask] = useCompleteWireFraudTaskMutation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isDocumentUnavailable, setIsDocumentUnavailable] = useState(false);
  const [saveErrorAttempt, setSaveErrorAttempt] = useState(0);
  const [fileUrl, setFileUrl] = useState("");
  const [fileName, setFileName] = useState(`${WIRE_FRAUD_DOCUMENT_TITLE}.pdf`);
  const fileUrlRef = useRef("");
  const completionInProgressRef = useRef(false);
  const hasAcknowledgedRef = useRef(false);

  const releaseFileUrl = useCallback(() => {
    if (fileUrlRef.current) {
      globalThis.URL.revokeObjectURL(fileUrlRef.current);
      fileUrlRef.current = "";
    }
    setFileUrl("");
  }, []);

  useEffect(
    () => () => {
      if (fileUrlRef.current) {
        globalThis.URL.revokeObjectURL(fileUrlRef.current);
      }
    },
    [],
  );

  const showDocument = (fileContents: string, contentType: string, name?: string) => {
    const blob = new Blob([base64ToUint8Array(fileContents)], { type: contentType });
    const objectUrl = globalThis.URL.createObjectURL(blob);
    fileUrlRef.current = objectUrl;
    setFileName(name ?? `${WIRE_FRAUD_DOCUMENT_TITLE}.pdf`);
    setFileUrl(objectUrl);
  };

  const trackWireFraudEvent = (
    eventName: (typeof WIRE_FRAUD_FS_EVENT)[keyof typeof WIRE_FRAUD_FS_EVENT],
  ) => {
    if (!nhDealApplicationId || !userId) {
      return;
    }

    const eventKey = `${userId}:${nhDealApplicationId}:${eventName}`;
    if (
      sessionWireFraudEventKeys.has(eventKey)
      || hasPersistedWireFraudFullStoryEvent(userId, nhDealApplicationId, eventName)
    ) {
      return;
    }

    sessionWireFraudEventKeys.add(eventKey);
    if (!canTrackWireFraudFullStory(featureFlag?.wireFraudDocumentEnabled === true)) {
      sessionWireFraudEventKeys.delete(eventKey);
      return;
    }

    try {
      const fullStoryProps = {
        email,
        sessionId: sessionStorage.getItem(DASHBOARD_SESSION_ID),
        dealId: dealId ?? null,
        prospectId: prospectId ?? null,
        encompassLoanGuid: encompassLoanId || loanId || null,
        encompassLoanNumber: loanNumber || null,
        nhDealApplicationId,
        loanPurpose: loanPurpose || null,
        milestone: loanProgress?.status ?? null,
        wasLoAssigned: Boolean(loanOfficerId),
        borrowerType: mapUqualBorrowerType(userRole, applicationRoleType),
        loName: loanOfficer?.fullName ?? null,
      };

      if (eventName === WIRE_FRAUD_FS_EVENT.started) {
        trackFSWireFraudTaskStarted(fullStoryProps);
      } else {
        trackFSWireFraudTaskCompleted(fullStoryProps);
      }
      persistWireFraudFullStoryEvent(userId, nhDealApplicationId, eventName);
    } catch {
      sessionWireFraudEventKeys.delete(eventKey);
      clearPersistedWireFraudFullStoryEvent(userId, nhDealApplicationId, eventName);
    }
  };

  const handleReviewDocument = async () => {
    if (!isCompleted) {
      trackWireFraudEvent(WIRE_FRAUD_FS_EVENT.started);
    }

    setIsDocumentUnavailable(false);
    setIsDrawerOpen(true);
    releaseFileUrl();

    if (!nhDealApplicationId || !userId) {
      setIsDocumentUnavailable(true);
      return;
    }

    try {
      const document = await retrieveWireFraudDocument({
        nhDealApplicationId,
        userId,
      }).unwrap();
      showDocument(document.fileContents, document.contentType, document.name);
    } catch {
      setIsDocumentUnavailable(true);
    }
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    releaseFileUrl();
  };

  const handleCloseDrawer = async () => {
    if (isCompleted || !fileUrl || isFetching || hasAcknowledgedRef.current) {
      closeDrawer();
      return;
    }

    if (completionInProgressRef.current || !nhDealApplicationId || !userId) {
      return;
    }

    completionInProgressRef.current = true;
    setIsCompleting(true);

    try {
      await completeWireFraudTask({ nhDealApplicationId, userId }).unwrap();
      trackWireFraudEvent(WIRE_FRAUD_FS_EVENT.completed);
      hasAcknowledgedRef.current = true;
      closeDrawer();
      await onAcknowledged?.();
    } catch {
      setSaveErrorAttempt((attempt) => attempt + 1);
      closeDrawer();
      return;
    } finally {
      completionInProgressRef.current = false;
      setIsCompleting(false);
    }
  };

  const handleDownload = () => {
    if (!fileUrl) {
      return;
    }

    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.click();
  };

  const showDocumentUnavailable = isDocumentUnavailable && !fileUrl;

  return (
    <section className="wire-fraud-panel">
      {!isDesktop && !isTablet && (
        <button
          className="wire-fraud-panel__back"
          onClick={() => setShowUpload(false)}
          type="button"
        >
          <img src={chevronLeft} alt="" />
          <span>Back to tasks</span>
        </button>
      )}

      <div className="wire-fraud-panel__content">
        <h5 className="wire-fraud-panel__title paragraph-medium-medium">
          {WIRE_FRAUD_TASK_TITLE}
        </h5>
        <p className="wire-fraud-panel__description paragraph-medium-regular">
          {isCompleted ? WIRE_FRAUD_COMPLETED_DESCRIPTION : WIRE_FRAUD_DETAIL_DESCRIPTION}
        </p>
        <Button
          label={isCompleted ? "View Document" : "Review Document"}
          onClick={() => void handleReviewDocument()}
          disabled={isFetching}
          className="wire-fraud-panel__review-button"
          fsElement={isCompleted ? undefined : WIRE_FRAUD_FS_EVENT.started}
        />
      </div>

      <Drawer
        isOpen={isDrawerOpen}
        onClose={() => void handleCloseDrawer()}
        title={WIRE_FRAUD_DOCUMENT_TITLE}
        customCss="preview-document"
        type={DrawerType.DocumentPreview}
        centerContent={isFetching}
        footerText={isCompleted ? undefined : WIRE_FRAUD_DETAIL_DESCRIPTION}
        closeText={isCompleted || showDocumentUnavailable ? "Close" : "Done"}
        closeDisabled={isCompleting}
        footerActionDisabled={isFetching || isCompleting || (!fileUrl && !showDocumentUnavailable)}
        footerFsElement={isCompleted || showDocumentUnavailable ? undefined : WIRE_FRAUD_FS_EVENT.completed}
      >
        {isFetching && <Spinner isApi />}
        {showDocumentUnavailable && !isFetching && (
          <div className="wire-fraud-panel__unavailable-state">
            <div className="wire-fraud-panel__unavailable-toolbar">
              {isDesktop ? (
                <p className="wire-fraud-panel__unavailable-page paragraph-small-regular">
                  Page 1 / 4
                </p>
              ) : (
                <button
                  type="button"
                  className="wire-fraud-panel__unavailable-download"
                  disabled
                >
                  Download
                </button>
              )}
            </div>
            <div className="wire-fraud-panel__unavailable" role="alert">
              <p className="wire-fraud-panel__unavailable-title paragraph-small-medium">
                <FontAwesomeIcon
                  className="wire-fraud-panel__unavailable-icon"
                  icon={regular("triangle-exclamation")}
                />
                {WIRE_FRAUD_DOCUMENT_UNAVAILABLE}
              </p>
              <p className="wire-fraud-panel__unavailable-description paragraph-small-regular">
                {WIRE_FRAUD_DOCUMENT_UNAVAILABLE_DESCRIPTION}
              </p>
            </div>
            <div className="wire-fraud-panel__unavailable-placeholder" aria-hidden="true">
              <FontAwesomeIcon icon={regular("file-lines")} />
            </div>
          </div>
        )}
        {!isFetching && fileUrl && (
          <DocumentPreview
            fileUrl={fileUrl}
            fileName={fileName}
            handleDownload={handleDownload}
          />
        )}
      </Drawer>
      {saveErrorAttempt > 0 && <WireFraudSaveErrorToast key={saveErrorAttempt} />}
    </section>
  );
};

export default WireFraudPanel;
